export type SupportedLang = "fr" | "en";

export const SUPPORTED_LANGS: readonly SupportedLang[] = ["fr", "en"] as const;

export interface LocalizedSlug {
  fr: string;
  en: string;
}

export const ROUTE_SLUGS = {
  vitrines: {
    fr: "sites-vitrines",
    en: "showcase",
  },
  whyWebsite: {
    fr: "pourquoi-un-site-web",
    en: "why-a-website",
  },
  audit: {
    fr: "audit-migration",
    en: "audit-migration",
  },
  blog: {
    fr: "blog",
    en: "blog",
  },
} as const satisfies Record<string, LocalizedSlug>;

export type RouteKey = keyof typeof ROUTE_SLUGS;

export const isSupportedLang = (value: string): value is SupportedLang =>
  (SUPPORTED_LANGS as readonly string[]).includes(value);

export const getLocalizedPath = (key: RouteKey, lang: SupportedLang): string =>
  `/${lang}/${ROUTE_SLUGS[key][lang]}`;

// ── Blog / auth path builders ────────────────────────────────────────────────
// The blog list lives in ROUTE_SLUGS (localized + SEO-aware). Article and auth
// paths take URL params, so they are built here instead. `login` uses a fixed
// (non-localized) segment under the `/:lang/` tree. Blog authoring now lives in
// the external CovalPress CMS, so there is no in-site admin area.

export const LOGIN_SLUG = "login";

/** Localized home (`/:lang/`). */
export const getHomePath = (lang: SupportedLang): string => `/${lang}/`;

/** Public blog list for a language (`/:lang/blog`). */
export const getBlogPath = (lang: SupportedLang): string =>
  getLocalizedPath("blog", lang);

/** Public blog article by slug (`/:lang/blog/:slug`). */
export const getBlogArticlePath = (lang: SupportedLang, slug: string): string =>
  `${getBlogPath(lang)}/${encodeURIComponent(slug)}`;

/** Login page (`/:lang/login`). */
export const getLoginPath = (lang: SupportedLang): string =>
  `/${lang}/${LOGIN_SLUG}`;

/**
 * Relative route patterns (under the `/:lang/*` parent) consumed by the router's
 * `<Route path>` declarations. Single source of truth shared with the absolute
 * path builders above — derived from the same `ROUTE_SLUGS` / segment constants,
 * so route paths are never hardcoded inline in `App.tsx`.
 */
export const ROUTE_PATHS = {
  blog: ROUTE_SLUGS.blog.fr,
  blogArticle: `${ROUTE_SLUGS.blog.fr}/:slug`,
  login: LOGIN_SLUG,
} as const;

/** Site root: `LangRedirect` sends it to the detected language. */
export const ROOT_PATH = "/";

/** Parent pattern that mounts every localized page (`/en/…`, `/fr/…`). */
export const LOCALIZED_ROUTE_PATTERN = "/:lang/*";

/**
 * Dev-only render canvases, mounted outside `/:lang/*` and captured to PNG or
 * PDF by the `scripts/capture-*` scripts (see `scripts/CAPTURE.md`). A path
 * changed here must be changed in its capture script too.
 */
export const EXPORT_PATHS = {
  linkedInBanner: "/linkedin-banner",
  xBanner: "/x-banner",
  linkedInPost: "/linkedin-post/export",
  instaStory: "/insta-story/export",
  resume: "/resume/export",
  onePager: "/one-pager/export",
  harness: "/harness/export",
  upworkCover: "/upwork-cover/export",
} as const;

/** Narrows a possibly-undefined resolved language to a supported one. */
export const resolveLang = (value: string | undefined | null): SupportedLang =>
  value && isSupportedLang(value) ? value : "en";

export const findRouteKeyBySlug = (slug: string): RouteKey | null => {
  for (const [key, definition] of Object.entries(ROUTE_SLUGS)) {
    if (definition.fr === slug || definition.en === slug) {
      return key as RouteKey;
    }
  }
  return null;
};

export const swapSlugForLang = (
  slug: string,
  targetLang: SupportedLang,
): string => {
  const key = findRouteKeyBySlug(slug);
  return key ? ROUTE_SLUGS[key][targetLang] : slug;
};
